import chatgptLogo from "@static/img/chatgpt-logo.png";
import claudeLogo from "@static/img/claude-logo.png";
import { AssessmentState, AssessmentType } from "types/graphql-schema";

import {
  MCP_DOCS_URL,
  getMcpServerUrl,
} from "@apps/account/components/integrations/mcp-constants";

// Hides the button for every organization until its release.
export const REVIEW_AI_TOOL_FLAG = "review-ai-tool";

export const AI_TOOLS = [
  { name: "Claude", url: "https://claude.ai/new", logo: claudeLogo },
  { name: "ChatGPT", url: "https://chatgpt.com/", logo: chatgptLogo },
] as const;

export type ReviewForAiTool = {
  assessmentId: number;
  isSelf: boolean;
};

export const canCompleteInAiTool = ({
  assessmentType,
  state,
  canUpdate,
  isResponder,
}: {
  assessmentType: AssessmentType;
  state: AssessmentState;
  canUpdate: boolean;
  isResponder: boolean;
}) =>
  waffle.flag_is_active(REVIEW_AI_TOOL_FLAG) &&
  assessmentType === AssessmentType.Performance &&
  state !== AssessmentState.Submitted &&
  canUpdate &&
  isResponder;

/**
 * One paragraph, the task first and the safeguards last. "Tools, not the
 * page": a tool with the person's browser would fill in the form around the
 * MCP. No names or titles: a person could write instructions into their own
 * name. Setup steps live on the docs page, so they change without a deploy.
 */
export const getReviewAiToolPrompt = (
  { assessmentId, isSelf }: ReviewForAiTool,
  review: string,
  origin: string,
) => {
  const which = isSelf ? `my self ${review}` : `my ${review}`;
  const whose = isSelf ? "I" : `the person I'm reviewing`;
  return [
    `Help me complete ${which} in Topicflow: ${origin}/assessments/assessment/${assessmentId}.`,
    `Use Topicflow's tools, not the page, to get the questions and what ${whose} worked on this period, plus anything useful in my other connected tools (meeting notes, docs, tickets).`,
    `Draft an answer to each question with me, one at a time, and save it when I'm happy.`,
    `Only submit when I ask.`,
    `If you don't have Topicflow's tools, don't guess the questions: tell me to set up Topicflow first, following ${MCP_DOCS_URL}, with the server address ${getMcpServerUrl(origin)}.`,
    `If Topicflow refuses the ${review}, tell me the reason it gives.`,
  ].join(" ");
};

/** Opens a new chat in the tool, with the prompt already in it. */
export const getAiToolUrl = (url: string, prompt: string) =>
  `${url}?q=${encodeURIComponent(prompt)}`;
